import React, {
  memo,
  MutableRefObject,
  RefObject,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { AppState } from "../../types";
import { ExcalidrawStickyNotesElement } from "../../element/types";
import { getFontString, sceneCoordsToViewportCoords, viewportCoordsToSceneCoords } from "../../utils";
import clsx from "clsx";
import { DEFAULT_FONT_SIZE, POINTER_EVENTS } from "../../constants";
import Quill from "quill";
import { mutateElement } from "../..";
import { SizeClass, SizeStyle } from "quill/formats/size";
import mitt from "mitt";
import Scene from "../../scene/Scene";
import "./StickyNote.scss";
import { useExcalidrawSetAppState } from "../App";
import { calculateScrollCenter } from "../../scene";
import {
  DEFAULT_STICK_NOTE_HEIGHT,
  EVENT_STICKYNOTE_SELECTION_CHANGE,
  getElementSelectionBorderColor
} from "../../../../../src/Const";
import {HoveredElementCircle} from "../HoveredElementCircle/HoveredElementCircle";
import {postMessage} from "../../../../../src/common/event";
import {isFileHtml, isHTML} from "../../../../../src/utils";

export type EmitterEvents = {
  "selection-change": any;
};
export const StickyNoteEmitter = mitt<EmitterEvents>();
export let oldRange: any = null;
const startValue = 10;
const endValue = 100;

const length = endValue - startValue + 1;

const pxOptions = Array.from({ length }, (_, index) => {
  const value = startValue + index;
  return `${value}px`;
});

SizeClass.whitelist = pxOptions;
SizeStyle.whitelist = pxOptions;
Quill.register(SizeStyle, true);
Quill.register(SizeClass, true);

export const storageSelection: any = false;

//juejin.cn/post/7441577870383300645
// https:
export const QuillCache = new Map();
let autoSize = true;

interface StickyNoteProps {
  stickyNotesElement: ExcalidrawStickyNotesElement;
  appState: Readonly<AppState>;
  onWheel:any,
}

const StickyNote = memo(
  ({ stickyNotesElement: el, appState,onWheel }: StickyNoteProps) => {
    const container = useRef<HTMLDivElement>(null);
    const quill = useRef<any>(null);
    const { x, y } = useMemo(
      () =>
        sceneCoordsToViewportCoords({ sceneX: el.x, sceneY: el.y }, appState),
      [appState, el.x, el.y],
    );
    const isActive = useMemo(() => {
      return (
        appState.activeStickyNotes?.element?.id === el?.id &&
        appState.activeStickyNotes?.state === "active"
      );
    }, [
      appState.activeStickyNotes?.element,
      appState.activeStickyNotes?.state,
      el,
    ]);
    const setAppState = useExcalidrawSetAppState();
    useEffect(() => {
      if (!quill.current) {
        return;
      }
      autoSize = true;
      mutateElement(el, {
        isActive,
      });
      if (isActive && container.current) {
        const textArea = container.current.children[0]!;
        if (textArea.scrollHeight > textArea.clientHeight) {
          autoSize = false;
        }
      }
      if (
        Object.keys(appState.selectedElementIds).length === 1 &&
        appState.selectedElementIds[el.id]
      ) {
        const length = quill.current.getLength();
        const range = quill.current.getSelection() || {
          index: 0,
          length: length - 1,
        };
        StickyNoteEmitter.emit("selection-change", { range, id: el.id });
        if (!isActive) {
          quill.current.blur();
        } else {
          const appHeight = appState.height + appState.offsetTop
          const containerRect = container.current?.getBoundingClientRect()!
          if (containerRect.bottom > appHeight && length > 1) {
            setAppState((appState) => ({
              ...calculateScrollCenter([el], appState),
            }), () => {
              quill.current.setSelection(length - 1);
            });
          } else {
            quill.current.setSelection(length - 1);

          }
        }
      }

      return () => { };
    }, [el, isActive, appState.selectedElementIds]);

    useEffect(() => {
      if (!container.current || quill.current) {
        return;
      }
      quill.current = new Quill(container.current, {
        modules: {
          toolbar: false,
        },
      });

      QuillCache.set(el.id, quill.current);
      quill.current.format("size", `${16}px`);
      if (el.value) {
        let text = el.value;
        if (!el.riched) {
          text = "";
          const lines = el.value.split("\n");
          lines.forEach((line, index) => {
            text +=
              `<p>` +
              `<span class="ql-size-${el.fontSize}px"` +
              `style="color: ${el.strokeColor};">${line}</span>` +
              `</p>`;
          });
        }
        if (isHTML(text)){
          quill.current.clipboard.dangerouslyPasteHTML(text);
        }else{
          quill.current.setText(text);
        }
      }

      quill.current.blur();
      setTimeout(() => {
        if (isActive)
        {
          const length = quill.current.getLength();
          quill.current.setSelection(length - 1);
        }
      }, 100);

      quill.current.on("selection-change", (range: any, oldRange1: any) => {
        oldRange = { id: el.id, oldRange: oldRange1 };
        if (!range) {
          return;
        }
        StickyNoteEmitter.emit("selection-change", { range, id: el.id });
        postMessage(EVENT_STICKYNOTE_SELECTION_CHANGE,{range:range})
      });

      quill.current.on(
        "text-change",
        (delta: any, _oldDelta: any, source: string) => {
          if (
            delta.ops.find((op: { insert: string }) =>
              ["\n", "\r"].includes(op.insert),
            )
          ) {
            const range = quill.current.getSelection();
            const { size } = quill.current.getFormat(range);
            setTimeout(() => {
              quill.current.format("size", size || `${16}px`);
            }, 10);
          }

          if (source === "api") {
            setTimeout(() => {
              changeEditor(container, el, appState, quill);
            }, 10);
          } else {
            changeEditor(container, el, appState, quill);
          }

        },
      );
      return () => { };
    }, []);

    return (
        <div
            className={clsx("excalidraw__mindmap-container")}
            id={`excalidraw__mindmap-container${el.id}`}
            style={{
              transform: `translate(${x - appState.offsetLeft}px, ${y - appState.offsetTop
              }px) scale(${appState.zoom.value})`,
              display: "block",
              opacity: el.opacity / 100,
            }}
        >
          <div
              className="excalidraw__mindmap-container__inner"
              id={`excalidraw__mindmap-container__inner${el.id}`}
              style={{
                width: `${el.width}px`,
                height: `${el.height}px`,
                transform: `rotate(${el.angle}rad)`,
              }}
          >
            <div
                className="excalidraw__mindmap__outer"
                id={`excalidraw__mindmap__outer${el.id}`}
                style={{
                  // height: `${el.height}px`,
                  pointerEvents: isActive
                      ? POINTER_EVENTS.enabled
                      : POINTER_EVENTS.disabled,
                }}
            >
              <div
                  className="quill-container"
                  onCut={(e) => e.stopPropagation()}
                  onCopy={(e) => e.stopPropagation()}
                  ref={container}
                  placeholder=""
                  style={{
                    height: `${el.height}px`,
                    borderRadius: "10px",
                    resize: "none",
                    backgroundColor: el.backgroundColor,
                    // @ts-ignore
                    textAlign: el.textAlign,
                    border: "none",
                    padding: `16px`,
                    whiteSpace:'pre',
                    color:`${el.strokeColor}`,
                    font: getFontString({
                      fontSize: el.fontSize || DEFAULT_FONT_SIZE,
                      fontFamily: el.fontFamily,
                      textareaType: "all",
                    }),
                  }}
                  onWheel={(event)=>{
                      if (quill.current?.scroll.domNode.scrollHeight < el.height){
                          onWheel(event);
                      }
                  }}
              ></div>
            </div>

            <div style={{
              width: '100%', height: `100%`, left: '0', top: '0', position: 'absolute',zIndex:'1000',
              border: (appState.selectedElementIds[el.id]) ? `${2.2 / appState.zoom.value}px solid ${getElementSelectionBorderColor()}` : 'none',
              borderRadius: '8px', pointerEvents: 'none'
            }}></div>

          </div>


          {/*//四个hoverd点*/}
          <HoveredElementCircle state={appState} el={el}/>
        </div>
    );
  },
);

export default StickyNote;

function clamp({value, max = document.body.offsetHeight - 102}: { value: number; max?: number }) {
  const min = DEFAULT_STICK_NOTE_HEIGHT;
  // const max = document.body.offsetHeight - 102;
  return Math.max(min, Math.min(value, max));
}

function changeEditor(
    container: RefObject<HTMLDivElement>,
    el: ExcalidrawStickyNotesElement,
    appState: Readonly<AppState>,
    quill: MutableRefObject<any>,
) {
  const html = quill.current?.getSemanticHTML();
  const element = Scene.getScene(el)?.getElement(
    el.id,
  )! as ExcalidrawStickyNotesElement;

  const appHeight = appState.height + appState.offsetTop

  const containerRect = container.current?.getBoundingClientRect()!
  const textArea = container.current?.children[0]!;
  //  新增：
  // 1.如果到底部了，需要向上，修改y
  let newY = element.y
  let newHeight = element.height
  if (containerRect.bottom > appHeight) {
    const { y } = viewportCoordsToSceneCoords({ clientX: 0, clientY: appHeight }, appState)
    newY = y - element.height - 10;
  }

  if (autoSize) {
    // 2.如果没有到底部，则只需要增加高度
    if (textArea.scrollHeight > textArea.clientHeight) {
      newHeight = clamp({
        value: textArea.scrollHeight + 50,
      });
      //到底了且超出高度
      if (newY !== element.y) {
        newY = newY - newHeight + element.height
      }
    } else {
      const children: any = textArea.children || [];
      let totalChildrenHeight = 0;
      for (let i = 0; i < children.length; i++) {
        const style = window.getComputedStyle(children[i]);
        totalChildrenHeight += children[i].offsetHeight;
        totalChildrenHeight +=
          parseFloat(style.marginTop) + parseFloat(style.marginBottom);
      }
      if (totalChildrenHeight < textArea.clientHeight) {
        newHeight = clamp({
          value: totalChildrenHeight + 50,
        });
      }
    }
  }

  mutateElement(element, {
    y: newY,
    height: newHeight,
    value: html
  });


  setTimeout(() => {
    const containerRect = container.current?.getBoundingClientRect()!
    if (containerRect.bottom > appHeight) {
      const { y } = viewportCoordsToSceneCoords({ clientX: 0, clientY: appHeight }, appState)
      const newY = y - element.height - 10;
      mutateElement(element, {
        y: newY,
      });
    }
  }, 0);







}
